import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

export const customThemePreset = definePreset(Aura, {
  semantic: {
    fontFamily: 'cursive',
    primary: {
      50: '#afc7ed',
      100: '#cfdef4',
      200: '#afc7ed',
      300: '#8fb1e6',
      400: '#6e9bdf',
      500: '#2C69C7',
      600: '#2e6ed1',
      700: '#275db1',
      800: '#204c91',
      900: '#193b70',
      950: '#122a50',
    },
    info: {
      50: '#afc7ed',
      100: '#cfdef4',
      200: '#afc7ed',
      300: '#8fb1e6',
      400: '#6e9bdf',
      500: '#2C69C7',
      600: '#2e6ed1',
      700: '#275db1',
      800: '#204c91',
      900: '#193b70',
      950: '#122a50',
    },

    secondary: {
      500: '#d9d9d9',
    },

    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      sm: {
        fontSize: '0.875rem',
        paddingX: '0.625rem',
        paddingY: '0.375rem',
      },
      lg: {
        fontSize: '1.125rem',
        paddingX: '0.875rem',
        paddingY: '0.625rem',
      },
      borderRadius: '{border.radius.md}',
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: 'none',
      },
      transitionDuration: '{transition.duration}',
    },
    list: {
      padding: '0.25rem 0.25rem',
      gap: '2px',
      header: {
        padding: '0.5rem 1rem 0.25rem 1rem',
      },
      option: {
        padding: '0.5rem 0.75rem',
        borderRadius: '{border.radius.sm}',
      },
      optionGroup: {
        padding: '0.5rem 0.75rem',
        fontWeight: '600',
      },
    },
    content: {
      borderRadius: '{border.radius.md}',
    },
    mask: {
      transitionDuration: '0.15s',
    },
    navigation: {
      list: {
        padding: '0.25rem 0.25rem',
        gap: '2px',
      },
      item: {
        padding: '0.5rem 0.75rem',
        borderRadius: '{border.radius.sm}',
        gap: '0.5rem',
      },
      submenuLabel: {
        padding: '0.5rem 0.75rem',
        fontWeight: '600',
      },
      submenuIcon: {
        size: '0.875rem',
      },
    },
    overlay: {
      select: {
        borderRadius: '{border.radius.md}',
        shadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
      popover: {
        borderRadius: '{border.radius.md}',
        padding: '0.75rem',
        shadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
      modal: {
        borderRadius: '{border.radius.xl}',
        padding: '1.25rem',
        shadow:
          '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      },
      navigation: {
        shadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
    },
    colorScheme: {
      light: {
        focusRing: {
          width: '1px',
          style: 'solid',
          color: '{primary.color}',
          offset: '2px',
          shadow: 'none',
        },
        info: {
          color: '{info.500}',
          contrastColor: '#ffffff',
          hoverColor: '{info.600}',
          activeColor: '{info.700}',
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
        },
        highlight: {
          background: '#3e67c5',
          focusBackground: '#3e67c5',
          color: '#ffff',
          focusColor: '#ffff',
        },
        secondary: {
          color: '#d9d9d9',
          inverseColor: '#000000',
          hoverColor: '#cfcfcf',
          activeColor: '#bfbfbf',
        },
        text: {
          primary: {
            hoverBackground: '{primary.50}',
            activeBackground: '{primary.100}',
            color: '{primary.color}',
          },
        },
        mask: {
          background: 'rgba(0,0,0,0.4)',
          color: '{primary.200}',
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.200}',
          filledBackground: '{surface.50}',
          filledHoverBackground: '{surface.50}',
          filledFocusBackground: '{surface.50}',
          borderColor: '{surface.300}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.400}',
          color: '{surface.700}',
          disabledColor: '{surface.500}',
          placeholderColor: '{surface.500}',
          invalidPlaceholderColor: '{red.600}',
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.600}',
          floatLabelActiveColor: '{surface.500}',
          floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
          iconColor: '{surface.400}',
          shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)',
        },
        navigation: {
          list: {
            padding: '0.25rem 0.25rem',
            gap: '2px',
          },
          item: {
            padding: '0.5rem 0.75rem',
            borderRadius: '{border.radius.sm}',
            gap: '0.5rem',
          },
          submenuLabel: {
            padding: '0.5rem 0.75rem',
            fontWeight: '600',
          },
          submenuIcon: {
            size: '0.875rem',
          },
        },
      },
      dark: {
        primary: {
          color: '{primary.400}',
          contrastColor: 'white',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}',
        },
        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{primary.700}',
          focusColor: '{primary.800}',
        },
      },
    },
  },
});
